// Booking Types for My Tickets functionality

import type { TableCategory } from './tableBookingTypes';
import type { BusinessResponse, MediaResponse, EventScheduleResponse, EventBookingCategoryResponse } from './eventBookingCategoryTypes';
import type { RepeatedSchedule } from '@/features/events/types/types';
import type { CartResponse } from '@/features/cart/types';
import type { PublicUserResponse } from '@/features/user/types/types';

export interface BookingUser {
  id: string;
  first_name: string;
  last_name: string;
  dob: string;
  email: string;
  phone_number: {
    country_code: string;
    number: string;
  };
  gender: string;
  profile_picture_id: string | null;
}

export interface BookingCategory {
  id: string;
  name: string;
  description: string;
  default_capacity: number | null;
  default_price: number | null;
  default_cancellation_fees: number | null;
  is_table_booking: boolean;
  default_custom_slot_duration: number | null;
  table_categories: TableCategory[] | null;
  card_image_id: string | null;
  card_video_id: string | null;
  media: MediaResponse[] | null;
  business: BusinessResponse | null;
}

export interface Event {
  id: string;
  name: string;
  description: string;
  last_minute_cancellation_duration: number;
  redemption_window_duration: number;
  event_type: string;
  repeated_schedule: RepeatedSchedule | null;
  published: boolean;
  card_image_id: string | null;
  card_video_id: string | null;
  media: MediaResponse[] | null;
  business: BusinessResponse | null;
  booking_categories: BookingCategory[] | null;
}

export interface EventSchedule {
  id: string;
  start_time: string;
  duration: number;
  cancelled: boolean;
  event: Event;
  event_booking_categories: EventBookingCategory[] | null;
}

export interface EventBookingCategory {
  id: string;
  cancelled: boolean;
  conflict_detected: boolean;
  capacity: number;
  remaining_capacity: number;
  price: number;
  cancellation_fees: number;
  event_schedule: EventSchedule;
  booking_category: BookingCategory;
}

export interface Booking {
  id: string;
  status: number;
  start_time: string;
  duration: number;
  booked_table_number: number | null;
  event_booking_category: EventBookingCategory;
  user: BookingUser;
  redeemer: unknown | null;
}

// BookingCategoryClass enum
export type BookingCategoryClass = 'Diamond' | 'Platinum' | 'Gold' | 'Silver' | 'Bronze' | 'Standard';

// New API response types
export interface BookingResponse {
  id: string;
  status: string; // "Active", "Cancelled", etc.
  price: number;
  currency: string;
  timezone_id: string;
  booking_category_class: BookingCategoryClass;
  start_time: string;
  duration: number; // Duration in nanoseconds
  early_entry_duration: number | null; // Early entry duration in nanoseconds
  booked_table_number: string | null;
  booked_table_size: number | null;
  event_booking_category: EventBookingCategoryResponse | null;
  user: PublicUserResponse | null;
  cart: CartResponse | null;
  redeemer: Record<string, unknown> | null; // TODO: Define BusinessUserResponse type
  redeemed_at: string | null;
  add_on_redeemed: boolean;
  transfer_id: string | null;
  add_ons: BookingAddOnResponse[] | null;
}

export interface BookingAddOnResponse {
  add_on: {
    id: string;
    name: string;
    description: string;
    published: boolean;
    default_price: number | null;
    default_inventory_count: number | null;
    image_id: string | null;
    business: BusinessResponse | null;
    is_platform_add_on: boolean;
  };
  price: number;
  quantity: number;
  remaining_quantity: number;
  action_history: {
    created_by: {
      user_id: string;
      user_name: string;
      action_type: number;
      acted_at: string;
      rank: number;
    };
    actions: unknown[] | null;
  };
}

export interface GroupedScheduleResponse {
  event_schedule: EventScheduleResponse;
  bookings: Array<{ booking: BookingResponse }>;
}

export interface GroupedCartResponse {
  cart: CartResponse;
  bookings: Array<{ booking: BookingResponse }>;
}

export interface BookingResponseWithQRCode {
  booking: BookingResponse;
  qrcode: string;
  add_on_qrcodes: Record<string, string>; // Map of addon ID to QR code string
}

// Legacy types for backward compatibility
export interface Ticket {
  booking: Booking;
  qrcode: string;
}

export interface TicketsResponse {
  data: Ticket[];
}

// Status constants for booking status
export const BookingStatus = {
  ACTIVE: 'ACTIVE',
  REDEEMED: 'REDEEMED',
  CANCELLED_BY_USER_WITH_REFUND: 'CANCELLED_BY_USER_WITH_REFUND',
  CANCELLED_BY_USER_WITHOUT_REFUND: 'CANCELLED_BY_USER_WITHOUT_REFUND',
  CANCELLED_BY_BUSINESS: 'CANCELLED_BY_BUSINESS',
  EXPIRED: 'EXPIRED',
  PENDING_PAYMENT: 'PENDING_PAYMENT',
  ABANDONED: 'ABANDONED',
  DISPUTED: 'DISPUTED',
  DISPUTE_LOST: 'DISPUTE_LOST',
} as const;

export type BookingStatus = typeof BookingStatus[keyof typeof BookingStatus];

// Utility type for ticket display
export interface TicketDisplayData {
  id: string;
  eventName: string;
  categoryName: string;
  startTime: string;
  price: number;
  user: BookingUser;
  qrcode: string;
  bookingId: string;
}

// Grouped event schedule instance
export interface EventScheduleInstance {
  eventScheduleId: string;
  event: Event;
  scheduleStartTime: string;
  scheduleDuration: number;
  cardImageId: string | null;
  tickets: Ticket[];
}

// Grouped tickets by event schedule
export interface GroupedTickets {
  [key: string]: EventScheduleInstance;
}
